import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, User, Phone, Mail, Users, Calendar as Cal, MessageSquare, ArrowRight, Check, Sparkles, Anchor } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRUISE_TYPES = [
 "Day Cruise (12–4pm)",
 "Evening Cruise (6–10pm)",
 "Birthday Cruise",
 "Wedding Cruise",
 "Engagement Party",
 "Hens Party",
 "Corporate Event",
 "Christmas Party",
 "NYE Cruise",
 "Vivid Cruise",
 "BYO Cruise",
 "Private Charter",
];

export default function BookingForm({ selectedDate, onClear }) {
 const [form, setForm] = useState({
  name: "",
  phone: "",
  email: "",
  guests: 20,
  cruise_type: "Day Cruise (12–4pm)",
  message: "",
 });
 const [submitting, setSubmitting] = useState(false);
 const [success, setSuccess] = useState(null); // { name, cruiseType, date }

 const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

 const submit = async (e) => {
  e.preventDefault();
  if (!selectedDate) {
   toast.error("Please pick a date from the calendar first.");
   return;
  }
  if (!form.name || !form.email) {
   toast.error("Name and email are required.");
   return;
  }
  setSubmitting(true);
  try {
   await axios.post(`${API}/enquiries`, {
    ...form,
    guests: String(form.guests ?? ""),
    event_type: form.cruise_type,
    event_date: selectedDate,
    source_page: "home-availability",
   });
   const firstName = form.name.trim().split(/\s+/)[0];
   setSuccess({
    name: firstName,
    cruiseType: form.cruise_type,
    date: new Date(selectedDate + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
   });
   toast.custom(() => (
    <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-[#0A1E3D] via-[#0F2A57] to-[#0A1E3D] text-white pl-3 pr-5 py-3 ring-1 ring-amber-300/40 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] backdrop-blur">
     <div className="grid place-items-center w-9 h-9 rounded-full bg-amber-300 text-[#0A1E3D]">
      <Check className="w-5 h-5" strokeWidth={3} />
     </div>
     <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold">Slot reserved</div>
      <div className="text-sm font-semibold">On hold for {firstName} &middot; we&apos;ll confirm in 24 hrs</div>
     </div>
    </div>
   ), { duration: 6000 });
   setForm({ name: "", phone: "", email: "", guests: 20, cruise_type: "Day Cruise (12–4pm)", message: "" });
   if (onClear) onClear();
  } catch (err) {
   toast.error("Something went wrong. Please call 1300 100 502.");
  } finally {
   setSubmitting(false);
  }
 };

 const friendlyDate = selectedDate
  ? new Date(selectedDate + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
  : null;

 // ─── SUCCESS STATE ───────────────────────────────────────────────────────────
 if (success) {
  return (
   <div
    className="relative overflow-hidden bg-white rounded-2xl ring-1 ring-amber-200/60 shadow-xl p-7 lg:p-9 text-center"
    style={{ background: "radial-gradient(at 20% 0%, hsl(45,90%,93%) 0%, transparent 50%), radial-gradient(at 80% 100%, hsl(200,80%,92%) 0%, transparent 50%), linear-gradient(180deg, #fff 0%, hsl(35,42%,97%) 100%)" }}
    data-testid="booking-success"
   >
    {/* confetti dots */}
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
     {[
      { l: "8%", t: "12%", c: "bg-amber-300", s: "w-2 h-2", d: "0s" },
      { l: "22%", t: "70%", c: "bg-pink-400", s: "w-2.5 h-2.5", d: ".4s" },
      { l: "78%", t: "20%", c: "bg-sky-400", s: "w-2 h-2", d: ".2s" },
      { l: "90%", t: "65%", c: "bg-amber-400", s: "w-3 h-3", d: ".6s" },
      { l: "50%", t: "8%", c: "bg-purple-400", s: "w-2 h-2", d: ".3s" },
      { l: "12%", t: "45%", c: "bg-emerald-400", s: "w-2 h-2", d: ".5s" },
      { l: "85%", t: "42%", c: "bg-pink-300", s: "w-2 h-2", d: ".1s" },
     ].map((p, i) => (
      <span
       key={i}
       className={`absolute ${p.c} ${p.s} rounded-full opacity-80`}
       style={{ left: p.l, top: p.t, animation: `book-float 3.4s ${p.d} ease-in-out infinite` }}
      />
     ))}
    </div>

    <style>{`
     @keyframes book-float { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-14px) rotate(20deg); } }
     @keyframes book-pop { 0% { transform: scale(0) rotate(-45deg); opacity: 0; } 60% { transform: scale(1.15) rotate(10deg); opacity: 1; } 100% { transform: scale(1) rotate(0); opacity: 1; } }
     @keyframes book-ring { 0% { transform: scale(0.6); opacity: 0.55; } 100% { transform: scale(1.6); opacity: 0; } }
    `}</style>

    {/* animated check */}
    <div className="relative inline-grid place-items-center mb-5">
     <span className="absolute inset-0 rounded-full bg-amber-300/40" style={{ animation: "book-ring 2.2s ease-out infinite" }} />
     <span className="absolute inset-0 rounded-full bg-amber-300/30" style={{ animation: "book-ring 2.2s ease-out 0.6s infinite" }} />
     <div className="relative grid place-items-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-[0_15px_40px_-10px_rgba(252,211,77,0.7)]" style={{ animation: "book-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both" }}>
      <Anchor className="w-9 h-9 text-[#0A1E3D]" strokeWidth={2.5} />
     </div>
    </div>

    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-pink-600 font-bold mb-2">
     <Sparkles className="w-3.5 h-3.5" /> Slot reserved
    </div>
    <h3 className="font-display text-3xl text-ink leading-[1.05]" data-testid="booking-success-headline">
     Magic incoming, <span className="text-pink italic">{success.name}</span>.
    </h3>
    <p className="text-ink-mute text-sm mt-4 max-w-md mx-auto leading-relaxed">
     We&apos;ve put a 24-hour hold on your {" "}
     <span className="font-semibold text-ink">{success.cruiseType}</span> for {" "}
     <span className="font-semibold text-ink">{success.date}</span>.
     Our crew will reach out shortly with pricing and the link to lock it in.
    </p>

    {/* mini summary */}
    <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-2">
     <span className="inline-flex items-center gap-2 rounded-full bg-white/85 ring-1 ring-black/5 px-3.5 py-1.5 text-xs font-semibold text-ink">
      <Cal className="w-3.5 h-3.5 text-pink" /> {success.date.split(",")[0]}
     </span>
     <span className="inline-flex items-center gap-2 rounded-full bg-white/85 ring-1 ring-black/5 px-3.5 py-1.5 text-xs font-semibold text-ink">
      <Anchor className="w-3.5 h-3.5 text-amber-500" /> {success.cruiseType}
     </span>
     <span className="inline-flex items-center gap-2 rounded-full bg-white/85 ring-1 ring-black/5 px-3.5 py-1.5 text-xs font-semibold text-ink">
      <Check className="w-3.5 h-3.5 text-emerald-500" /> No payment yet
     </span>
    </div>

    {/* secondary CTAs */}
    <div className="mt-6 flex flex-wrap justify-center gap-3">
     <a
      href="tel:1300100502"
      data-testid="booking-success-call-btn"
      className="inline-flex items-center gap-2 rounded-full bg-[#0A1E3D] hover:bg-[#0F2A57] text-white font-bold uppercase tracking-[0.18em] text-[11px] px-5 py-3 transition-colors"
     >
      <Phone className="w-3.5 h-3.5" /> Call 1300 100 502
     </a>
     <button
      type="button"
      onClick={() => setSuccess(null)}
      data-testid="booking-success-pick-another-btn"
      className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-amber-50 text-[#0A1E3D] font-bold uppercase tracking-[0.18em] text-[11px] px-5 py-3 ring-1 ring-black/10 transition-colors"
     >
      Pick another date
     </button>
    </div>
   </div>
  );
 }

 // ─── DEFAULT FORM STATE ──────────────────────────────────────────────────────
 return (
  <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl ring-1 ring-[hsl(220,18%,90%)] p-6 lg:p-8 flex flex-col" data-testid="booking-form">
   <h3 className="font-display text-2xl lg:text-3xl text-ink mb-5">Your details</h3>

   {!selectedDate ? (
    <div className="flex items-start gap-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3.5 mb-6" data-testid="booking-form-prompt">
     <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
     <div className="text-sm text-amber-900">Pick a date and time slot from the calendar to continue.</div>
    </div>
   ) : (
    <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-3 mb-6 flex items-center justify-between gap-3" data-testid="booking-form-date">
     <div className="text-sm text-emerald-900">
      <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 block mb-0.5">Selected date</span>
      {friendlyDate}
     </div>
     {onClear && (
      <button type="button" onClick={onClear} className="text-xs font-bold text-emerald-800 hover:text-emerald-900 underline" data-testid="booking-clear-date">Change</button>
     )}
    </div>
   )}

   <div className="grid grid-cols-2 gap-4">
    <div>
     <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-ink-mute mb-1.5"><User className="w-3 h-3" /> Name</label>
     <input className="field" value={form.name} onChange={e=>update('name', e.target.value)} placeholder="Your name" required data-testid="booking-name" />
    </div>
    <div>
     <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-ink-mute mb-1.5"><Phone className="w-3 h-3" /> Phone</label>
     <input className="field" value={form.phone} onChange={e=>update('phone', e.target.value)} placeholder="04xx xxx xxx" data-testid="booking-phone" />
    </div>
   </div>

   <div className="mt-4">
    <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-ink-mute mb-1.5"><Mail className="w-3 h-3" /> Email</label>
    <input type="email" className="field" value={form.email} onChange={e=>update('email', e.target.value)} placeholder="you@email.com" required data-testid="booking-email" />
   </div>

   <div className="grid grid-cols-2 gap-4 mt-4">
    <div>
     <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-ink-mute mb-1.5"><Users className="w-3 h-3" /> Guests</label>
     <input type="number" min="1" max="120" className="field" value={form.guests} onChange={e=>update('guests', e.target.value)} required data-testid="booking-guests" />
    </div>
    <div>
     <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-ink-mute mb-1.5"><Cal className="w-3 h-3" /> Cruise Type</label>
     <select className="field" value={form.cruise_type} onChange={e=>update('cruise_type', e.target.value)} data-testid="booking-cruise-type">
      {CRUISE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
     </select>
    </div>
   </div>

   <div className="mt-4">
    <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-ink-mute mb-1.5"><MessageSquare className="w-3 h-3" /> Tell us more</label>
    <textarea rows={3} className="field" value={form.message} onChange={e=>update('message', e.target.value)} placeholder="Catering preferences, departure wharf, special requests..." data-testid="booking-message" />
   </div>

   <button
    type="submit"
    disabled={submitting}
    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-[hsl(225,80%,14%)] font-black text-sm tracking-widest uppercase shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-[1.01] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    data-testid="booking-submit"
   >
    {submitting ? "Reserving…" : "Reserve This Slot"} <ArrowRight className="w-4 h-4" />
   </button>
   <p className="text-[11px] text-ink-mute text-center mt-3">We&apos;ll hold your slot and confirm within 24 hours. No payment required now.</p>

   <div className="mt-5 pt-5 border-t border-[hsl(220,18%,90%)] flex items-center justify-center gap-5 text-xs text-ink-mute">
    <a href="tel:1300100502" className="inline-flex items-center gap-1.5 hover:text-pink"><Phone className="w-3 h-3 text-pink" /> (02) 9999 9999</a>
    <a href="mailto:hello@magiccruises.com.au" className="inline-flex items-center gap-1.5 hover:text-pink"><Mail className="w-3 h-3 text-pink" /> hello@magiccruises.com.au</a>
   </div>
  </form>
 );
}
